// Generated by Cursor Grok 4.6 on 2026-10-12.
// Pure helpers for the public 1a board: search, area filter, open/fixed split,
// and sort by carriers affected. Kept out of the page so tests do not need DOM.

import type { Issue, Status } from "@workspace/api-client-react";

export type BoardBucket = "open" | "fixed";

const RESOLVED_RE = /\b(resolved|fixed|closed|done)\b/;

export function isResolvedStatus(
  status: Pick<Status, "key" | "label"> | undefined,
): boolean {
  if (!status) return false;
  return RESOLVED_RE.test(`${status.key} ${status.label}`.toLowerCase());
}

export function issueMatchesQuery(
  issue: Pick<Issue, "id" | "title" | "description">,
  query: string,
  categoryName?: string | null,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  // "#12" means issue 12 exactly, so "#1" does not also list #10 through #19.
  if (/^#\d+$/.test(q)) return q === `#${issue.id}`;
  const hay = [issue.title, issue.description, categoryName ?? "", `#${issue.id}`]
    .join(" ")
    .toLowerCase();
  return hay.includes(q);
}

export function sortByMostAffected<T extends Pick<Issue, "id" | "affectedCount" | "updatedAt">>(
  issues: T[],
): T[] {
  return [...issues].sort((a, b) => {
    const ac = a.affectedCount ?? 0;
    const bc = b.affectedCount ?? 0;
    if (bc !== ac) return bc - ac;
    const at = new Date(a.updatedAt).getTime();
    const bt = new Date(b.updatedAt).getTime();
    if (bt !== at) return bt - at;
    return a.id - b.id;
  });
}

export function filterBoardIssues(
  issues: Issue[],
  opts: {
    query: string;
    categoryId: number | null;
    bucket: BoardBucket;
    statusById: Map<number, Status>;
    categoryNameOf: (issue: Issue) => string | undefined;
  },
): Issue[] {
  const matched = issues.filter((issue) => {
    if (opts.categoryId != null && issue.categoryId !== opts.categoryId) {
      return false;
    }
    if (!issueMatchesQuery(issue, opts.query, opts.categoryNameOf(issue))) {
      return false;
    }
    const resolved = isResolvedStatus(opts.statusById.get(issue.statusId));
    return opts.bucket === "fixed" ? resolved : !resolved;
  });
  return sortByMostAffected(matched);
}

export function countBuckets(
  issues: Issue[],
  opts: {
    query: string;
    categoryId: number | null;
    statusById: Map<number, Status>;
    categoryNameOf: (issue: Issue) => string | undefined;
  },
): { open: number; fixed: number } {
  let open = 0;
  let fixed = 0;
  for (const issue of issues) {
    if (opts.categoryId != null && issue.categoryId !== opts.categoryId) continue;
    if (!issueMatchesQuery(issue, opts.query, opts.categoryNameOf(issue))) continue;
    if (isResolvedStatus(opts.statusById.get(issue.statusId))) fixed += 1;
    else open += 1;
  }
  return { open, fixed };
}
